import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  BRAND,
  MODE_LABELS,
  INVITE_CODE_LEN,
  NICKNAME_MAX,
  NUDGE_COOLDOWN_MS,
  ROOM_TTL_LABEL,
  SPREADS,
  TOPICS,
  TOPIC_LABELS,
  validateNickname,
  type Mbti,
  type Mode,
  type RoomParticipant,
  type Topic,
} from '@tarot/shared';
import {
  CrisisBlockedError,
  QuotaExceededError,
  apiGet,
  createRoom,
  resolveInviteCode,
} from '../../lib/api';
import { QuotaGate, useQuotaBlocked, type QuotaBlock } from '../quota/QuotaNotice';
import { useRefreshQuota } from '../quota/useQuota';
import { useTrack } from '../../lib/track';
import { useRoomStore } from './store';
import { forgetSeat, recallSeat } from './seat';
import { CardFan, slotGridClass, slotLabelClass } from '../../components/CardFan';
import { TarotCard } from '../../components/TarotCard';
import { TopicIcon } from '../../components/TopicIcon';
import { CaptureSignature, ReadingActions } from '../../components/ReadingActions';
import { MagicText } from '../../components/MagicText';
import { CardMeaning } from '../../components/CardMeaning';
import { BellIcon, ClockIcon, RefreshIcon, ShareIcon } from '../../components/icons';
import { canShareSheet, shareLink } from '../../lib/share';
import { HomeLink } from '../../components/HomeLink';
import { ActionStack, PrimaryButton, SecondaryButton } from '../../components/Buttons';
import { MbtiField } from '../../components/MbtiPicker';
import { ConcernField } from '../../components/ConcernField';
import { CrisisNotice } from '../../components/CrisisNotice';
import { publicOrigin } from '../../lib/platform';
import { useMe } from '../auth/useMe';
import { usePublishReading } from '../reading/useAutoSave';
import { useScrollToReading } from '../reading/followScroll';
import { TossBanner } from '../../components/TossBanner';
import { SaveCta } from '../reading/SaveCta';
import { ReviewCta } from '../reading/ReviewCta';
import { AnimatePresence } from 'framer-motion';
import { LogoMark } from '../../brand/BrandLogo';

/**
 * 코드 입력칸의 이름 — 로비 제목과 초대 안내 문구가 **같은 말**을 써야 한다.
 * 어긋나면 "'코드로 입장'이 어디 있냐"고 되묻게 된다.
 */
const CODE_ENTRY_LABEL = '초대코드로 입장';

/**
 * 커플·우정 방 (설계서 §5.3). 로그인 불필요 — 비회원도 사용 가능 (2026-08-18 정책).
 * - 링크 공유(/room/:id) → 닉네임만 입력하면 바로 입장
 * - 초대코드 입력 입장 (길이는 shared INVITE_CODE_LEN)
 * - 카드 뽑기는 각자, 뽑는 즉시 전원에게 실시간 공개(card:revealed)
 * - 새로고침·앱 전환 후에는 저장해 둔 닉네임으로 자동 재입장해 원래 좌석을 되찾는다 (seat.ts)
 */
export function RoomPage() {
  const { roomId } = useParams();
  return roomId ? <RoomView roomId={roomId} /> : <RoomLobby />;
}

/* ───────────────────────── 로비: 방 만들기 / 코드 입장 ───────────────────────── */

function RoomLobby() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const mode = (sp.get('mode') === 'friends' ? 'friends' : 'couple') as 'couple' | 'friends';
  useTrack(`menu:${mode}`);
  const [topic, setTopic] = useState<Topic | null>(null);
  const [concern, setConcern] = useState(''); // 고민 한 줄(선택) — 방장이 적어 전원 해석에 반영
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 하루 이용 횟수 초과 안내 (방 생성 = 해석 1회 차감)
  const [quotaBlock, setQuotaBlock] = useState<QuotaBlock | null>(null);
  /** 위기 발화로 방 생성이 되돌아옴 — 방 대신 상담 창구를 보여준다 (shared safety.ts) */
  const [crisis, setCrisis] = useState(false);
  const quotaBlocked = useQuotaBlocked(quotaBlock); // 소진 시 방 만들기 자체를 막음
  const refreshQuota = useRefreshQuota();

  const onCreate = async () => {
    // ⚠️ quotaBlocked로 막지 말 것 — 광고 게이트의 onAdWatched가 이 함수를 부르는데, 그 시점
    // 클로저의 quotaBlocked는 아직 true라 광고를 보고도 방이 만들어지지 않는다 (SoloPage와 동일).
    // 버튼 잠금은 disabled가 담당하고, 진짜 막힌 상태면 서버가 402/429를 다시 준다.
    if (!topic) return;
    setBusy(true);
    setError(null);
    setQuotaBlock(null);
    setCrisis(false);
    try {
      const { roomId, inviteCode } = await createRoom({
        mode,
        topic,
        maxSeats: MODE_LABELS[mode].maxSeats,
        ...(concern.trim() ? { concern: concern.trim() } : {}),
      });
      navigate(`/room/${roomId}`, { state: { inviteCode } });
    } catch (e) {
      if (e instanceof CrisisBlockedError) {
        setCrisis(true); // 방을 만들지 않는다 — 고민은 참여자 전원의 해석에 들어간다
      } else if (e instanceof QuotaExceededError) {
        setQuotaBlock({ message: e.message, status: e.status, reason: e.reason });
      } else {
        setError('방을 만들지 못했어요. 잠시 후 다시 시도해 주세요.');
      }
    } finally {
      setBusy(false);
      refreshQuota();
    }
  };

  const onEnterCode = async () => {
    const c = code.trim().toUpperCase();
    if (c.length !== INVITE_CODE_LEN) return;
    setBusy(true);
    setError(null);
    try {
      const { roomId } = await resolveInviteCode(c);
      navigate(`/room/${roomId}`);
    } catch {
      setError('코드가 올바르지 않거나 만료됐어요.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6">
      <h2 className="text-center text-2xl font-bold">{MODE_LABELS[mode].ko} 타로</h2>

      {/* 방 만들기 */}
      <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <h3 className="font-semibold">새 방 만들기</h3>
        {/* 나 혼자와 동일한 2열 — 아이콘+라벨 가로 배치로 칸 높이는 낮게 */}
        <ul className="grid grid-cols-2 gap-2">
          {TOPICS.map((t) => (
            <li key={t} className={TOPIC_LABELS[t].featured ? 'col-span-2' : ''}>
              <button
                onClick={() => setTopic(t)}
                className={`flex w-full items-center justify-center gap-2 rounded-xl border px-3 py-2 transition ${
                  topic === t
                    ? 'border-gold bg-gold/15'
                    : 'border-white/10 bg-white/5 hover:border-gold/50'
                }`}
              >
                <TopicIcon topic={t} className="h-7 w-7 shrink-0 text-gold" />
                <span className="text-sm font-medium">{TOPIC_LABELS[t].ko}</span>
              </button>
            </li>
          ))}
        </ul>
        {topic && <ConcernField value={concern} onChange={setConcern} />}
        {crisis && <CrisisNotice footer />}
        <QuotaGate block={quotaBlock} onAdWatched={onCreate} />
        <PrimaryButton disabled={!topic || busy || quotaBlocked} onClick={onCreate}>
          방 만들기 (
          {MODE_LABELS[mode].minSeats === MODE_LABELS[mode].maxSeats
            ? `${MODE_LABELS[mode].maxSeats}명`
            : `${MODE_LABELS[mode].minSeats}~${MODE_LABELS[mode].maxSeats}명`}
          )
        </PrimaryButton>
      </section>

      {/* 코드 입장 */}
      <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <h3 className="font-semibold">{CODE_ENTRY_LABEL}</h3>
        <div className="flex gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={INVITE_CODE_LEN}
            placeholder={`${INVITE_CODE_LEN}자리 코드`}
            className="min-w-0 flex-1 rounded-xl border border-white/15 bg-night-2 px-4 py-3 text-center text-lg font-semibold tracking-[0.35em] placeholder:text-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-moon-dim/60 focus:border-gold/60 focus:outline-none"
          />
          <button
            onClick={onEnterCode}
            disabled={busy || code.trim().length !== INVITE_CODE_LEN}
            className="rounded-xl bg-gold px-5 font-semibold text-night transition hover:bg-gold-deep disabled:opacity-40"
          >
            입장
          </button>
        </div>
      </section>

      {error && <p className="text-center text-sm text-red-300">⚠️ {error}</p>}

      <HomeLink />
      {/* 화면 맨 아래 — 방 만들기·코드 입장 조작부와 떨어뜨린다 */}
      <TossBanner />
    </div>
  );
}

/* ───────────────────────── 방: 대기 → 뽑기 → 해석 ───────────────────────── */

function RoomView({ roomId }: { roomId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const inviteCode = (location.state as { inviteCode?: string } | null)?.inviteCode;
  const {
    status,
    state,
    mySeat,
    aiText,
    aiUsage,
    errorMsg,
    nudgeFrom,
    reveal,
    join,
    start,
    pick,
    sendNudge,
    clearError,
    leave,
  } = useRoomStore();
  const { me: authUser } = useMe();
  const [nickname, setNickname] = useState('');
  const [nickError, setNickError] = useState<string | null>(null);
  // MBTI(선택): 회원은 내 정보에 저장된 값이 기본 선택되고, 이 방에서만 바꿀 수도 있음
  const [mbtiPick, setMbtiPick] = useState<Mbti | null>(null);
  const [mbtiOpen, setMbtiOpen] = useState(false);
  const [mbtiTouched, setMbtiTouched] = useState(false);
  // 해석 본문이 마지막 어절까지 드러났는지 (SoloPage와 동일 — 잘린 이미지 저장 방지)
  const [revealed, setRevealed] = useState(false);
  // 입장 전 방 요약 (모드별 타이틀 + 사라진 방 감지)
  const [roomMode, setRoomMode] = useState<Mode | null>(null);
  const [roomGone, setRoomGone] = useState(false);
  const captureRef = useRef<HTMLDivElement | null>(null); // 공유 캡처 영역

  // 해석 완료 → 회원이면 지난 타로 자동 저장 (공유 버튼은 같은 shareId 재사용)
  const shareData =
    state && state.phase === 'done' && aiText
      ? {
          mode: state.mode,
          topic: state.topic,
          spreadId: state.spreadId,
          participants: state.participants.map((p) => ({
            seat: p.seat,
            nickname: p.nickname,
            cards: p.cards,
          })),
          interpretation: aiText,
          ...aiUsage,
        }
      : null;
  const savedShareId = usePublishReading(shareData);

  useEffect(() => () => leave(), [leave]); // 페이지 이탈 시 소켓 정리

  /**
   * 단계가 바뀌면 새 화면이므로 맨 위에서 시작 (SoloPage와 동일 — 라우트 이동이 아니라 직접 처리).
   * 단 해석 단계는 예외 — 아래 useScrollToReading이 본문 첫 줄로 데려간다.
   * 둘 다 돌면 맨 위로 갔다가 다시 내려와 화면이 튄다.
   */
  useEffect(() => {
    if (state?.phase === 'interpreting' || state?.phase === 'done') return;
    window.scrollTo({ top: 0 });
  }, [state?.phase]);

  // 해석이 시작되면 본문 첫 줄이 화면 맨 위에 오도록 (참여자 카드가 화면을 다 채운다)
  const readingRef = useScrollToReading();

  // 로그인 사용자는 기본 닉네임·MBTI 자동 입력 (둘 다 이 방에서만 수정 가능)
  useEffect(() => {
    if (authUser?.nickname) setNickname((prev) => prev || authUser.nickname);
  }, [authUser]);
  useEffect(() => {
    if (authUser?.mbti && !mbtiTouched) setMbtiPick(authUser.mbti);
  }, [authUser, mbtiTouched]);

  useEffect(() => {
    let alive = true;
    apiGet<{ mode: Mode }>(`/rooms/${roomId}`)
      .then((info) => alive && setRoomMode(info.mode))
      .catch(() => alive && setRoomGone(true));
    return () => {
      alive = false;
    };
  }, [roomId]);

  const tryJoin = () => {
    // 닉네임 정책(10자·금지어) — shared validateNickname 단일 출처. 서버(RoomDO)에서도 재검증
    const check = validateNickname(nickname);
    if (!check.ok) return setNickError(check.reason);
    setNickError(null);
    clearError(); // 이전 서버 에러(중복 닉네임 등) 초기화
    // 좌석 기억은 스토어가 seat:assigned를 받을 때 저장한다 (좌석 증명 토큰까지 함께).
    // 여기서 미리 덮어쓰면 재입장 시 토큰이 지워져 "이미 사용 중인 닉네임"에 막힌다.
    join(roomId, check.value, mbtiPick ?? undefined);
  };

  /**
   * 새로고침·앱 전환으로 돌아왔을 때 자동으로 원래 좌석에 다시 앉는다.
   * 닉네임을 다시 묻지 않고, 서버(RoomDO)가 끊긴 좌석을 알아보고 돌려준다.
   * 한 번만 시도한다 — 실패하면 평소대로 닉네임 입력 화면이 뜬다.
   */
  useEffect(() => {
    // errorMsg가 있으면 멈춘다 — 서버가 거절한 뒤에도 다시 시도하면 무한 반복이 된다.
    // ref로 "한 번만" 막지 않는 이유: StrictMode가 effect를 두 번 돌릴 때 ref는 살아남아
    // 두 번째(진짜) 실행까지 막아버린다.
    if (status !== 'idle' || roomGone || errorMsg) return;
    const saved = recallSeat(roomId);
    if (!saved) return;
    setNickname(saved.nickname);
    join(roomId, saved.nickname, saved.mbti);
  }, [roomId, status, roomGone, errorMsg, join]);

  /** 좌석 기억을 버리고 로비로 — 방을 완전히 떠날 때만 쓴다 (재접속 복구가 엉뚱하게 걸리지 않게) */
  const leaveRoom = () => {
    forgetSeat();
    navigate('/room');
  };

  /**
   * ── 방이 사라진 경우: 바로 탈출 화면 ──
   * `status === 'gone'`은 방에 들어와 있다가 만료된 경우다 (30분 무활동 정리).
   * 이때 재연결 안내를 계속 띄우면 붙을 곳이 없는데 스피너만 도는 화면이 된다.
   */
  if ((roomGone || status === 'gone') && status !== 'joined') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg">방이 없어졌거나 만료됐어요 🕯️</p>
        <ActionStack>
          <PrimaryButton onClick={leaveRoom}>새 방 만들기 / 코드 입장으로</PrimaryButton>
          <HomeLink />
        </ActionStack>
      </div>
    );
  }

  // ── 입장 전: 닉네임 입력 ──
  if (status === 'idle' || status === 'connecting' || (status === 'closed' && mySeat === null)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <h2 className="text-2xl font-bold">
          {roomMode ? `${MODE_LABELS[roomMode].ko}방 입장` : '방에 입장하기'}
        </h2>
        {status === 'closed' && (
          <p className="text-sm text-red-300">
            방에 들어가지 못했어요. 방이 없어졌거나 이미 시작됐을 수 있어요.
          </p>
        )}
        <input
          value={nickname}
          onChange={(e) => {
            setNickname(e.target.value);
            setNickError(null);
          }}
          maxLength={NICKNAME_MAX}
          placeholder={`닉네임 (${NICKNAME_MAX}자 이하)`}
          className="w-64 rounded-xl border border-white/15 bg-night-2 px-4 py-3 text-center focus:border-gold/60 focus:outline-none"
          onKeyDown={(e) => e.key === 'Enter' && tryJoin()}
        />
        <p className="text-xs text-moon-dim/80">방에 함께 있는 사람들에게 보여지는 닉네임이에요</p>
        {/* MBTI(선택) — 회원은 내 정보에 저장된 값이 기본 선택됨 */}
        <div className="w-64">
          <MbtiField
            value={mbtiPick}
            onChange={(v) => {
              setMbtiTouched(true);
              setMbtiPick(v);
            }}
            open={mbtiOpen}
            onToggle={() => setMbtiOpen((o) => !o)}
            hint={
              authUser?.mbti && !mbtiTouched ? '내 정보에 저장된 값이에요' : '궁합 해석에 참고돼요'
            }
          />
        </div>
        {nickError && <p className="text-sm text-red-300">⚠️ {nickError}</p>}
        {!nickError && errorMsg && <p className="text-sm text-red-300">⚠️ {errorMsg}</p>}
        <ActionStack>
          <PrimaryButton disabled={!nickname.trim() || status === 'connecting'} onClick={tryJoin}>
            {status === 'connecting' ? '입장 중…' : status === 'closed' ? '다시 시도' : '입장하기'}
          </PrimaryButton>
          {status === 'closed' && (
            <SecondaryButton onClick={leaveRoom}>새 방 만들기 / 코드 입장으로</SecondaryButton>
          )}
          {/* 입장 실패(이미 시작된 방·중복 닉네임 등)에서도 빠져나갈 길을 항상 준다 */}
          {(status === 'closed' || errorMsg) && <HomeLink />}
        </ActionStack>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <p className="text-moon-dim">방 정보를 불러오는 중…</p>
        <ActionStack>
          <SecondaryButton onClick={leaveRoom}>방 만들기 화면으로</SecondaryButton>
          <HomeLink />
        </ActionStack>
      </div>
    );
  }

  if (status === 'closed') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <p className="text-lg">연결이 끊겼어요 🕯️</p>
        <p className="text-sm text-moon-dim">
          다시 입장하면 원래 자리와 뽑아둔 카드가 그대로 살아나요.
        </p>
        <ActionStack>
          {/* 좌석 기억(seat.ts)이 남아 있어 새로고침만 하면 자동으로 다시 앉는다 */}
          <PrimaryButton onClick={() => window.location.reload()}>
            <RefreshIcon className="h-5 w-5" /> 새로고침해서 다시 입장
          </PrimaryButton>
          <SecondaryButton onClick={leaveRoom}>새 방 만들기 / 코드 입장으로</SecondaryButton>
          <HomeLink />
        </ActionStack>
      </div>
    );
  }

  const spread = SPREADS[state.spreadId];
  const positionLabels = spread.positions.map((p) => p.nameKo);
  const me = state.participants.find((p) => p.seat === mySeat);
  const others = state.participants.filter((p) => p.seat !== mySeat);

  return (
    <div className="flex flex-1 flex-col gap-4">
      {/* 다시 연결 중 — 방 내용은 그대로 두고 위에만 알린다. 모바일에서 앱 전환만 해도 지나가는 구간이라
          화면을 갈아치우면 오히려 "튕겼다"는 인상을 준다 */}
      <AnimatePresence>
        {status === 'reconnecting' && (
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            className="fixed inset-x-0 top-[calc(1rem+env(safe-area-inset-top))] z-50 mx-auto flex w-fit items-center gap-2 rounded-full border border-white/15 bg-night-2 px-5 py-3 text-sm shadow-xl shadow-black/50"
          >
            <LogoMark className="tw-brand-spin h-4 w-4 text-gold" />
            <span className="text-moon-dim">다시 연결 중이에요… 자리는 그대로예요</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 재촉 알림 토스트 */}
      <AnimatePresence>
        {nudgeFrom && (
          <motion.div
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            // fixed라 AppShell의 safe-area 패딩이 안 먹는다 — 노치 아래로 직접 내린다
            className="fixed inset-x-0 top-[calc(1rem+env(safe-area-inset-top))] z-50 mx-auto flex w-fit items-center gap-2 rounded-full border border-gold/60 bg-night-2 px-5 py-3 text-sm shadow-xl shadow-black/50"
          >
            <BellIcon className="h-4 w-4 animate-bounce text-gold" />
            <span>
              <b className="text-gold">{nudgeFrom}</b>님이 기다리고 있어요 — 카드를 골라주세요!
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 상대가 카드를 공개했을 때 — 그 카드를 잠깐 띄운다.
          모바일에서는 상대 카드 영역이 접힘 아래라, 이게 없으면 실시간으로 함께 보고 있다는 걸
          영영 모른 채 끝난다. 재촉 토스트와 자리가 겹치지 않게 조금 아래에 둔다 */}
      <AnimatePresence>
        {reveal && (
          <motion.div
            initial={{ opacity: 0, y: -18, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -18, scale: 0.94 }}
            className={`fixed inset-x-0 z-40 mx-auto flex w-fit items-center gap-3 rounded-2xl border border-gold/40 bg-night-2 py-2 pl-2 pr-4 shadow-xl shadow-black/50 ${
              nudgeFrom
                ? 'top-[calc(5rem+env(safe-area-inset-top))]'
                : 'top-[calc(1rem+env(safe-area-inset-top))]'
            }`}
          >
            {/* ⚠️ 폭은 반드시 **감싼 요소**로 준다. TarotCard의 img가 `w-full`이라
                className으로 `w-11`을 넘겨도 어느 쪽이 이길지 순서에 달렸고,
                실제로 카드가 화면을 통째로 덮었다 (실측) */}
            <span className="block w-11 shrink-0">
              <TarotCard drawn={reveal.card} zoomable={false} className="shadow-none" />
            </span>
            <span className="text-sm">
              <b className="text-gold">{reveal.nickname}</b>님이 카드를 뒤집었어요
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 상단: 주제 칩 + 참여자 */}
      <div className="flex items-center justify-center gap-2">
        <div className="flex items-center gap-2.5 rounded-full border border-gold/30 bg-gold/10 px-5 py-2.5 text-base">
          <TopicIcon topic={state.topic} className="h-8 w-8 text-gold" />
          <span className="font-semibold">{TOPIC_LABELS[state.topic].ko}</span>
          <span className="text-moon-dim">· {MODE_LABELS[state.mode].ko}</span>
        </div>
      </div>
      <ParticipantChips
        participants={state.participants}
        maxSeats={state.maxSeats}
        mySeat={mySeat}
        // 대기 단계에서는 아직 뽑을 일이 없어 점을 숨긴다
        total={state.phase === 'waiting' ? 0 : spread.positions.length}
      />

      {errorMsg && (
        <button
          onClick={clearError}
          className="rounded-xl bg-red-500/15 px-4 py-2 text-sm text-red-300"
        >
          ⚠️ {errorMsg} (탭해서 닫기)
        </button>
      )}

      {/* 해석 실패 복구 — 실패하면 phase가 drawing으로 돌아오는데 전원이 이미 다 뽑은 상태라
          카드 뽑기로는 해석이 다시 시작되지 않는다. 방장의 재시도(room:start)가 유일한 길 */}
      {state.phase === 'drawing' &&
        state.participants.length > 0 &&
        state.participants.every((p) => p.cards.length >= spread.positions.length) && (
          <div className="flex flex-col items-center gap-2 rounded-3xl border border-white/10 bg-white/5 p-5 text-center">
            <p className="text-sm text-moon-dim">해석이 도중에 멈췄어요.</p>
            {me?.isHost ? (
              // 재시도는 서버가 상한(MAX_INTERPRET_TRIES)까지만 받아준다. 상한에 걸리면
              // room:error로 안내가 뜨므로, 그때 쓸 탈출구를 여기 함께 둔다
              <ActionStack>
                <PrimaryButton onClick={start}>다시 해석 요청하기</PrimaryButton>
                <SecondaryButton onClick={leaveRoom}>새 방 만들기 / 코드 입장으로</SecondaryButton>
              </ActionStack>
            ) : (
              <p className="text-sm text-moon-dim">방장이 다시 요청하면 이어서 볼 수 있어요.</p>
            )}
          </div>
        )}

      {/* 단계별 화면 */}
      {state.phase === 'waiting' && (
        <WaitingView
          roomId={roomId}
          inviteCode={inviteCode}
          mode={state.mode}
          isHost={!!me?.isHost}
          joined={state.participants.length}
          maxSeats={state.maxSeats}
          canStart={state.participants.length >= MODE_LABELS[state.mode].minSeats}
          onStart={start}
          expiresAt={state.expiresAt}
        />
      )}

      {state.phase === 'drawing' && (
        <>
          <CardFan
            count={spread.positions.length}
            positionLabels={positionLabels}
            title={spread.nameKo}
            // 재접속 복구 — 서버가 기억한 내 카드로 덱을 되살린다 (다시 뽑게 하면 안 된다)
            initialPicked={me?.cards}
            onPick={pick}
          />
          {others.length > 0 && (
            <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
              <h3 className="text-sm font-semibold text-moon-dim">함께 보는 사람들</h3>
              {others.map((p) => (
                <OtherRow
                  key={p.seat}
                  p={p}
                  total={spread.positions.length}
                  onNudge={() => sendNudge(p.seat)}
                />
              ))}
            </section>
          )}
        </>
      )}

      {(state.phase === 'interpreting' || state.phase === 'done') && (
        <>
          {/* 공유 캡처 영역: 전원의 덱 + 해석 + 서명 */}
          <div ref={captureRef} className="flex flex-col gap-4 rounded-2xl bg-night p-1">
            {state.participants.map((p) => (
              <section key={p.seat} className="flex flex-col gap-2">
                <h3 className="text-lg font-semibold text-gold">
                  {p.nickname}
                  {p.seat === mySeat && ' (나)'}
                </h3>
                <ul className={`grid ${slotGridClass(spread.positions.length)}`}>
                  {p.cards.map((d) => (
                    <li key={d.position} className="flex min-w-0 flex-col items-center">
                      <span className={slotLabelClass()}>{positionLabels[d.position]}</span>
                      <TarotCard drawn={d} />
                      <CardMeaning drawn={d} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <article
              ref={readingRef}
              className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/5 p-5 leading-relaxed"
            >
              {aiText ? (
                <MagicText
                  text={aiText}
                  active={state.phase === 'interpreting'}
                  onComplete={() => setRevealed(true)}
                />
              ) : (
                <span className="text-moon-dim">카드를 종합해 읽는 중…</span>
              )}
            </article>
            <CaptureSignature />
          </div>
          {/* 본문이 다 드러난 뒤에 노출 — 펼치는 중에 이미지를 저장하면 해석이 잘린다 */}
          {revealed && aiText && (
            <>
              <ReadingActions
                targetRef={captureRef}
                shareData={shareData}
                savedShareId={savedShareId}
                onOther={() => {
                  forgetSeat();
                  navigate('/');
                }}
              />
              {/* 비회원에게만 — 결과를 본 직후가 회원 전환율이 가장 높다 */}
              <SaveCta shareData={shareData} />
              {/* 회원에게만, 그리고 토스 미니앱에서만 — 저장 유도와 자리를 다투지 않는다 */}
              <ReviewCta />
              <TossBanner />
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ── 부속 컴포넌트 ── */

/** 카드 공개 뒤 칩이 반짝이는 시간 */
const CHIP_FLASH_MS = 900;

/**
 * 참여자 칩 — 항상 화면 위쪽에 보이는 유일한 자리다.
 *
 * 그래서 **진행도(뽑은 장수)를 여기에 싣는다.** 모바일에서는 상대 카드 영역이 접힘 아래로
 * 밀려(390×740에서 889px 지점) 누가 몇 장을 뽑았는지 알 길이 없다. 점 표시는 자리를
 * 거의 안 먹으면서 "지금 같이 진행 중"을 계속 보여준다. 새로 뽑히면 잠깐 반짝인다.
 */
function ParticipantChips({
  participants,
  maxSeats,
  mySeat,
  total,
}: {
  participants: RoomParticipant[];
  maxSeats: number;
  mySeat: number | null;
  /** 1인당 뽑을 카드 수 — 0이면 진행도를 숨긴다(대기 단계) */
  total: number;
}) {
  const [flash, setFlash] = useState<Set<number>>(new Set());
  const counts = useRef(new Map<number, number>());

  useEffect(() => {
    const grew: number[] = [];
    for (const p of participants) {
      const before = counts.current.get(p.seat) ?? 0;
      if (p.cards.length > before) grew.push(p.seat);
      counts.current.set(p.seat, p.cards.length);
    }
    if (!grew.length) return;
    setFlash(new Set(grew));
    const t = setTimeout(() => setFlash(new Set()), CHIP_FLASH_MS);
    return () => clearTimeout(t);
  }, [participants]);

  return (
    <ul className="flex flex-wrap justify-center gap-2">
      {Array.from({ length: maxSeats }, (_, i) => {
        const p = participants[i];
        const lit = p && flash.has(p.seat);
        return (
          <li
            key={i}
            className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition ${
              p ? 'border-gold/40 bg-gold/10' : 'border-dashed border-white/15 text-moon-dim/50'
            } ${lit ? 'border-gold bg-gold/25 shadow-[0_0_12px] shadow-gold/40' : ''}`}
          >
            {p ? (
              <>
                {p.isHost && <span aria-label="방장">👑</span>}
                <span className={p.connected ? '' : 'line-through opacity-50'}>{p.nickname}</span>
                {p.seat === mySeat && <span className="text-gold">· 나</span>}
                {total > 0 && (
                  <span
                    className="ml-0.5 flex items-center gap-0.5"
                    aria-label={`${p.cards.length}/${total}장 뽑음`}
                  >
                    {Array.from({ length: total }, (_, n) => (
                      <span
                        key={n}
                        className={`h-1.5 w-1.5 rounded-full ${
                          n < p.cards.length ? 'bg-gold' : 'bg-white/25'
                        }`}
                      />
                    ))}
                  </span>
                )}
              </>
            ) : (
              '빈 자리'
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** 만료 임박 강조 시작 시점 */
const EXPIRY_WARN_MS = 5 * 60 * 1000;

/** 남은 시간 표시 — 초대 링크·코드 만료까지 1초 단위 카운트다운 */
function ExpiryNotice({ expiresAt }: { expiresAt?: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  // 서버가 만료 시각을 안 준 옛 방이면 고정 문구로 폴백
  if (!expiresAt) {
    return (
      <p className="flex items-center justify-center gap-1.5 text-xs text-moon-dim/80">
        <ClockIcon className="h-3.5 w-3.5 shrink-0 text-gold/70" />이 링크와 코드는{' '}
        <b className="font-semibold text-gold">{ROOM_TTL_LABEL}</b> 동안 쓸 수 있어요
      </p>
    );
  }

  const left = Math.max(0, expiresAt - now);
  if (left === 0) {
    return (
      <p className="flex items-center justify-center gap-1.5 text-xs text-red-300">
        <ClockIcon className="h-3.5 w-3.5 shrink-0" />
        초대 링크와 코드가 만료됐어요 · 새 방을 만들어 주세요
      </p>
    );
  }

  const totalSec = Math.floor(left / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  const urgent = left <= EXPIRY_WARN_MS;

  return (
    <p
      className={`flex items-center justify-center gap-1.5 text-xs ${
        urgent ? 'text-red-300' : 'text-moon-dim/80'
      }`}
    >
      <ClockIcon className={`h-3.5 w-3.5 shrink-0 ${urgent ? '' : 'text-gold/70'}`} />
      링크·코드 남은 시간{' '}
      <b
        className={`font-semibold tabular-nums ${urgent ? 'text-red-300' : 'text-gold'}`}
        aria-live="off"
      >
        {mm}:{ss}
      </b>
    </p>
  );
}

function WaitingView({
  roomId,
  inviteCode,
  mode,
  isHost,
  joined,
  maxSeats,
  canStart,
  onStart,
  expiresAt,
}: {
  roomId: string;
  inviteCode?: string;
  mode: Mode;
  isHost: boolean;
  joined: number;
  maxSeats: number;
  canStart: boolean;
  onStart: () => void;
  expiresAt?: number;
}) {
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const [shareNote, setShareNote] = useState<string | null>(null);
  const link = `${publicOrigin()}/room/${roomId}`;

  const copy = async (kind: 'link' | 'code', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard 미지원 */
    }
  };

  const note = (result: Awaited<ReturnType<typeof shareLink>>, what: string) => {
    if (result === 'copied') setShareNote(`${what} 복사했어요 — 붙여넣어 보내주세요 🔗`);
    else if (result === 'failed') setShareNote('공유하지 못했어요. 아래에서 복사해 주세요 😢');
    if (result === 'copied' || result === 'failed') setTimeout(() => setShareNote(null), 2500);
  };

  /**
   * 링크 공유와 코드 공유를 **따로** 둔다.
   * 한 메시지에 링크와 코드를 같이 보내면, 받은 사람이 링크로 이미 들어와 놓고도
   * "코드는 어디에 넣느냐"고 되묻는다. 둘은 서로 대체 수단이지 함께 쓰는 게 아니다.
   */
  const shareLinkOnly = async () =>
    note(
      await shareLink({
        title: `${BRAND.nameKo} ${MODE_LABELS[mode].ko} 타로`,
        // ⚠️ "아래 링크" 같은 위치 표현 금지 — 카카오톡은 링크 카드를 **문구 위에** 붙인다.
        // 메신저마다 순서가 달라, 어디에 있든 통하는 말로 적는다.
        text: [
          `${MODE_LABELS[mode].ko} 타로 같이 볼래요? ${BRAND.emoji}`,
          `링크를 누르면 코드 없이 바로 들어와요 (${ROOM_TTL_LABEL} 안에)`,
        ].join('\n'),
        url: link,
      }),
      '초대 링크를',
    );

  /** 링크가 막히는 곳(카톡 링크 차단·다른 기기)에서만 쓰는 우회 경로 — 들어갈 위치까지 알려준다 */
  const shareCodeOnly = async () =>
    note(
      await shareLink({
        title: `${BRAND.nameKo} 초대코드`,
        text: [
          `${MODE_LABELS[mode].ko} 타로 같이 볼래요? ${BRAND.emoji}`,
          `초대코드 ${inviteCode}`,
          `${BRAND.nameKo}에서 '${CODE_ENTRY_LABEL}'에 넣어주세요 (${ROOM_TTL_LABEL} 안에)`,
        ].join('\n'),
        url: `${publicOrigin()}/room`,
      }),
      '초대코드를',
    );

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-4 rounded-3xl border border-white/10 bg-white/5 p-5"
    >
      {/* 우정 방: 실시간 인원 카운트 */}
      {mode === 'friends' && (
        <p className="text-center text-2xl font-bold text-gold">
          {joined} <span className="text-base font-normal text-moon-dim">/ {maxSeats}명</span>
        </p>
      )}
      <p className="text-center text-moon-dim">
        {mode === 'couple'
          ? '상대가 들어오면 자동으로 시작돼요 💫'
          : canStart
            ? '더 기다리거나, 지금 시작할 수 있어요 ✨'
            : '친구를 기다리고 있어요…'}
      </p>

      {/* 링크 초대 — 주 경로. 여기만 써도 끝난다는 걸 문구로 못박는다 */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold">링크로 초대하기</p>
        <p className="text-xs text-moon-dim">
          받은 사람이 <b className="text-moon">링크만 누르면</b> 바로 들어와요. 코드는 넣지 않아도
          돼요.
        </p>
        {/* 모바일에서만 — 카카오톡 등으로 바로 보낸다. PC는 아래 복사 버튼 그대로 */}
        {canShareSheet() && (
          <button
            onClick={shareLinkOnly}
            className="flex h-12 items-center justify-center gap-2 rounded-xl bg-gold px-4 font-semibold text-night transition hover:bg-gold-deep"
          >
            <ShareIcon className="h-5 w-5" /> 초대 링크 보내기
          </button>
        )}
        <button
          onClick={() => copy('link', link)}
          className="flex items-center justify-between gap-2 rounded-xl border border-white/15 bg-night-2 px-4 py-3 text-left text-sm transition hover:border-gold/50"
        >
          <span className="truncate text-moon-dim">{link}</span>
          <span className="shrink-0 text-gold">{copied === 'link' ? '복사됨 ✓' : '링크 복사'}</span>
        </button>
      </div>

      {/* 코드 초대 — 링크를 못 쓸 때의 우회 경로. 시각적으로도 아래·보조로 둔다 */}
      {inviteCode && (
        <div className="flex flex-col gap-2 border-t border-white/10 pt-4">
          <p className="text-sm font-semibold text-moon-dim">코드로 초대하기</p>
          <p className="text-xs text-moon-dim">
            링크를 쓰기 어려울 때만요. 상대가 <b className="text-moon">{CODE_ENTRY_LABEL}</b> 칸에
            넣으면 돼요.
          </p>
          {canShareSheet() && (
            <button
              onClick={shareCodeOnly}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 text-sm font-medium text-moon-dim transition hover:border-gold/50 hover:bg-gold/10 hover:text-moon"
            >
              <ShareIcon className="h-4 w-4" /> 초대코드 보내기
            </button>
          )}
          <button
            onClick={() => copy('code', inviteCode)}
            className="flex items-center justify-between gap-2 rounded-xl border border-white/15 bg-night-2 px-4 py-3 text-sm transition hover:border-gold/50"
          >
            <span className="text-lg font-bold tracking-[0.35em] text-gold">{inviteCode}</span>
            <span className="shrink-0 text-gold">
              {copied === 'code' ? '복사됨 ✓' : '코드 복사'}
            </span>
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {shareNote && <p className="text-center text-xs text-moon-dim">{shareNote}</p>}
        <ExpiryNotice expiresAt={expiresAt} />
      </div>

      {/* 커플: 자동 시작이라 버튼 없음 / 우정: 방장이 시작 */}
      {mode === 'friends' &&
        (isHost ? (
          <PrimaryButton disabled={!canStart} onClick={onStart}>
            {canStart ? '타로 시작하기' : '인원이 모이면 시작할 수 있어요'}
          </PrimaryButton>
        ) : (
          <p className="text-center text-sm text-moon-dim">방장이 시작하길 기다리는 중…</p>
        ))}
    </motion.section>
  );
}

function OtherRow({
  p,
  total,
  onNudge,
}: {
  p: RoomParticipant;
  total: number;
  onNudge: () => void;
}) {
  const [nudgeSent, setNudgeSent] = useState(false);
  const done = p.cards.length >= total;

  const nudge = () => {
    if (nudgeSent || done) return;
    onNudge();
    setNudgeSent(true);
    setTimeout(() => setNudgeSent(false), NUDGE_COOLDOWN_MS);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {/* 1줄: 닉네임 칩 + 재촉 벨 | 카드 수 */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1 text-sm font-medium">
            {p.nickname}
          </span>
          {!done && (
            <button
              onClick={nudge}
              disabled={nudgeSent}
              aria-label={`${p.nickname}님에게 재촉 알림 보내기`}
              title="빨리 골라달라고 알림 보내기"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gold/40 text-gold transition hover:bg-gold/15 disabled:opacity-40"
            >
              {nudgeSent ? <span className="text-xs">✓</span> : <BellIcon className="h-4 w-4" />}
            </button>
          )}
        </div>
        <span className="shrink-0 text-xs text-moon-dim">
          {p.cards.length}/{total}
        </span>
      </div>
      {/* 2줄: 선택된 카드 — 화면 폭 100% 균등 분할 */}
      <ul className={`grid w-full ${slotGridClass(total)}`}>
        {Array.from({ length: total }, (_, i) => {
          const d = p.cards.find((c) => c.position === i);
          return (
            <li key={i} className="min-w-0">
              {d ? (
                <motion.div
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                >
                  <TarotCard drawn={d} />
                </motion.div>
              ) : (
                <div className="aspect-[3/5] rounded-lg border border-dashed border-white/15 bg-white/5" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
